<template>
    <div class="px-6 py-5 overflow-y-auto right-content-grid-vs h-[calc(100vh-309px)]">
        <div class="text-[#414141] font-sans">
            <template v-if="isLoading">
                <div class="w-full h-full flex items-center justify-center">
                    <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00423D]"></div>
                </div>
            </template>
            <template v-else>
                <h3 class="text-[1.25rem] leading-[1.75rem] mb-[1rem] font-semibold">
                    Policies and Procedures Manual
                </h3>
                <p class="text-[14px] leading-[22px] mb-[1rem]">Below is a list of foundational policies and procedures that your organization should have in place if it is fundraising. Select the policies and procedures your organization needs to put into place or improve/revise</p>

                <!-- Checkboxes Container -->
                <div class="mt-6">
                    <!-- Select All Option -->
                    <div class="mb-4">
                        <div class="flex items-center">
                            <input
                                type="checkbox"
                                v-model="isAllPoliciesSelected"
                                @change="onSelectAllClick"
                                class="form-checkbox h-4 w-4 mt-1 cursor-pointer"
                            />
                            <span class="ml-2 text-[#00423D] font-semibold">SELECT ALL</span>
                        </div>
                    </div>

                    <!-- Individual Policies -->
                    <div v-for="(policy, idx) in policies" :key="idx" class="mb-6 ml-4">
                        <div class="flex items-start">
                            <input
                                type="checkbox"
                                v-model="policy.isSelected"
                                class="form-checkbox h-4 w-4 mt-1 cursor-pointer"
                            />
                            <div class="ml-2">
                                <span 
                                    class="text-[#00423D] font-semibold uppercase block cursor-pointer"
                                    @click="policy.isSelected = !policy.isSelected"
                                >{{ policy.name }}</span>
                                <p class="text-[14px] leading-[22px] text-[#414141] mt-2">{{ policy.description }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useStepValidation } from '@/composables/useStepValidation';
import { useAuthStore } from "@/stores/auth";
import { useUserProjectsStore } from "@/stores/projects";
import global from "@/global";
import { useRouter } from 'vue-router';
// Store instances
const authStore = useAuthStore();
const projectsStore = useUserProjectsStore();
const router = useRouter();

// State
const isLoading = ref(true);
const isAllPoliciesSelected = ref(false);
const policies = ref([
    {
        identity: "gift_acceptance_policy",
        name: "gift acceptance policy",
        description: "This policy specifies what types of gifts are accepted so that staff can narrow their focus to only those donations that further their mission.",
        isSelected: false,
        url: "/files/GiftAcceptancePolicy.docx",
        fileName: "Gift Acceptance Policy",
        collateralTitle: "Gift Acceptance Policy"
    },
    {
        identity: "gift_entry_and_recording_policy",
        name: "gift-entry and recording policy",
        description: "This policy, which also includes a Cash Handling Policy, outlines the steps in which donations should be received, recorded, deposited, acknowledged, and reported.",
        isSelected: false,
        url: "/files/GiftEntryAndRecordingPolicy.docx",
        fileName: "Gift Entry and Recording Policy.docx",
        collateralTitle: "Gift-Entry and Recording Policy"
    },
    {
        identity: "gift_acknowledgement_policy",
        name: "gift acknowledgement policy",
        description: "This policy, which also includes a Pledge Reminder Policy, further details exactly how gifts and pledges should be acknowledged and in what time frame.",
        isSelected: false,
        url: "/files/GiftAcknowledgmentPolicy.docx",
        fileName: "Gift Acknowledgment Policy",
        collateralTitle: "Gift Acknowledgement Policy"
    },
    {
        identity: "naming_rights_policy",
        name: "naming rights policy",
        description: "This policy illustrates what types of gifts can be considered for naming rights and highlights the associated parameters to which staff must adhere.",
        isSelected: false,
        url: "/files/NamingGiftsPolicy.docx",
        fileName: "Naming Gifts Policy",
        collateralTitle: "Naming Rights Policy"
    },
    {
        identity: "corporate_support_policy",
        name: "corporate support policy",
        description: "This policy lists the requirements that an organization must consider when seeking support from a corporate entity.",
        isSelected: false,
        url: "/files/CorporateSupportPolicy.docx",
        fileName: "Corporate Support Policy",
        collateralTitle: "Corporate Support Policy"
    },
    {
        identity: "donor_privacy_policy",
        name: "donor privacy policy",
        description: "This policy highlights the expectations of staff and board members (both current and former) when dealing with donors' sensitive information, and details how, when, and where this information should be used",
        isSelected: false,
        url: "/files/DonorPrivacyPolicy.docx",
        fileName: "Donor Privacy Policy",
        collateralTitle: "Donor Privacy Policy"
    }
]);

// Methods
const onSelectAllClick = () => {
    policies.value.forEach(policy => {
        policy.isSelected = isAllPoliciesSelected.value;
    });
};

// Watch for changes in policies to update select all state
watch(policies, (newPolicies) => {
    isAllPoliciesSelected.value = newPolicies.every(policy => policy.isSelected);
}, { deep: true });

// API Integration
async function fetchPolicies() {
    try {
        const res = await fetch(`${global.API_URL}/policies-and-procedures`, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${authStore.user.accessToken}`,
            },
        });
        
        if (!res.ok) {
            throw new Error(`HTTP error! Status: ${res.status}`);
        }
        
        const data = await res.json();
        if (data.foundational) {
            // Update the policies with fetched data
            policies.value.forEach(policy => {
                policy.isSelected = data.foundational[policy.identity] || false;
            });
        }
    } catch (error) {
        console.error("Failed to fetch policies:", error);
    }
}

async function savePolicies() {
    try {
        const policiesToSave = {};
        policies.value.forEach(policy => {
            policiesToSave[policy.identity] = policy.isSelected;
        });

        const res = await fetch(`${global.API_URL}/policies-and-procedures`, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${authStore.user.accessToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(policiesToSave),
        });
        
        if (!res.ok) {
            throw new Error(`HTTP error! Status: ${res.status}`);
        }
        
        const data = await res.json();
        if (!data.status) {
            throw new Error('Failed to save policies');
        }

        return true;
    } catch (error) {
        console.error("Failed to save policies:", error);
        return false;
    }
}

async function downloadSelectedPolicies() {
    // Download selected policy files
    policies.value.forEach(policy => {
        if (policy.isSelected && policy.url) {
            const link = document.createElement('a');
            link.href = policy.url;
            link.download = policy.fileName;
            link.click();
        }
    });

    // Call the increment download flag API
    try {
        const res = await fetch(`${global.API_URL}/policies_and_procedures/increment_download_flag`, {
            method: 'PATCH',
            headers: {
                Authorization: `Bearer ${authStore.user.accessToken}`,
            },
        });

        if (!res.ok) {
            throw new Error(`HTTP error! Status: ${res.status}`);
        }
    } catch (error) {
        console.error('Failed to update download flag:', error);
    }
}

// Use the validation composable with custom validation
useStepValidation({
    validateFn: async () => {
        // First save the policies
        const saveResult = await savePolicies();
        if (!saveResult) return false;

        // If save was successful, download selected policies
        await downloadSelectedPolicies();
        
        // Notify the store that data has been updated
        projectsStore.setQuestionUpdate();
        
        return true;
    }
});

onMounted(async () => {
    isLoading.value = true;
    await fetchPolicies();
    isLoading.value = false;
});
</script>

<style scoped>
.form-checkbox {
    @apply rounded;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    width: 16px;
    height: 16px;
    border: 2px solid #00423D;
    border-radius: 4px;
    background-color: white;
    cursor: pointer;
    position: relative;
    flex-shrink: 0;
}

.form-checkbox:checked {
    background-color: #00423D;
    border-color: #00423D;
}

.form-checkbox:checked::after {
    content: '';
    position: absolute;
    left: 4px;
    top: 1px;
    width: 4px;
    height: 8px;
    border: solid white;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
}


/* Loading animation */
@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.animate-spin {
    animation: spin 1s linear infinite;
}
</style> 